/**
 * Regras puras de captura e qualidade dos exemplares.
 * Mantidas fora da UI para permitir auditoria e simulações determinísticas.
 */

/**
 * Cada tier tem uma FAIXA contínua de multiplicador em vez de um valor fixo.
 * `mult` é apenas o valor nominal/representativo do tier (usado como fallback e
 * em filtros); a captura sorteia um número contínuo dentro de [min, max], então
 * um Lendário pode nascer 1.72 ou 1.88 em vez de sempre 1.80.
 * As faixas são contíguas e não se sobrepõem, e o nominal fica dentro da faixa —
 * assim exemplares legados (mult exato) continuam válidos na sua faixa.
 *
 * `obtain`:
 *   - 'wild'     → pode sair de captura selvagem (peso > 0 no sorteio).
 *   - 'breeding' → NÃO sai do selvagem; só é alcançável via breeding/shiny.
 * Escala final: Comum → Celestial saem do selvagem; Anciã e Secreto são
 * exclusivos de breeding (topos de prestígio).
 */
export const SPECIMEN_RARITIES = Object.freeze([
  Object.freeze({ name: 'Comum', mult: 1, min: 1.0, max: 1.04, color: '#a9b5ae', weight: 40, obtain: 'wild' }),
  Object.freeze({ name: 'Incomum', mult: 1.05, min: 1.05, max: 1.14, color: '#72c87c', weight: 30, obtain: 'wild' }),
  Object.freeze({ name: 'Raro', mult: 1.2, min: 1.15, max: 1.34, color: '#5aa5df', weight: 20, obtain: 'wild' }),
  Object.freeze({ name: 'Épico', mult: 1.5, min: 1.35, max: 1.64, color: '#aa6de1', weight: 8.45, obtain: 'wild' }),
  Object.freeze({ name: 'Lendário', mult: 1.8, min: 1.65, max: 1.94, color: '#e5a84e', weight: 1, obtain: 'wild' }),
  Object.freeze({ name: 'Mítico', mult: 2, min: 1.95, max: 2.29, color: '#e46474', weight: 0.5, obtain: 'wild' }),
  Object.freeze({ name: 'Celestial', mult: 2.5, min: 2.3, max: 2.79, color: '#efdf87', weight: 0.05, obtain: 'wild' }),
  Object.freeze({ name: 'Ancião', mult: 3.2, min: 2.8, max: 3.6, color: '#43d6b5', weight: 0, obtain: 'breeding' }),
  Object.freeze({ name: 'Secreto', mult: 4, min: 3.6, max: 4.5, color: '#8a5cf0', weight: 0, obtain: 'breeding' }),
]);

/** Tiers que realmente saem do selvagem (peso > 0). */
export const WILD_SPECIMEN_RARITIES = Object.freeze(
  SPECIMEN_RARITIES.filter((tier) => (tier.weight || 0) > 0),
);

/** Mapeia um multiplicador contínuo (ex.: vindo de breeding) para o tier dono da faixa. */
export function specimenTierForMult(mult) {
  const value = Number(mult) || 0;
  for (const tier of SPECIMEN_RARITIES) {
    if (value >= tier.min && value <= tier.max) return tier;
  }
  return value < SPECIMEN_RARITIES[0].min
    ? SPECIMEN_RARITIES[0]
    : SPECIMEN_RARITIES.at(-1);
}

/**
 * A qualidade do exemplar é independente da raridade visual.
 * Assim, um Lendário ainda costuma ter IV abaixo de 75%, enquanto um
 * Lendário com 88%+ permanece uma conquista rara.
 */
export const IV_QUALITY_BANDS = Object.freeze([
  Object.freeze({ name: 'regular', min: 20, max: 74, weight: 70 }),
  Object.freeze({ name: 'promissor', min: 75, max: 87, weight: 23 }),
  Object.freeze({ name: 'elite', min: 88, max: 94, weight: 6 }),
  Object.freeze({ name: 'perfeito', min: 95, max: 100, weight: 1 }),
]);

export const EARLY_CAPTURE_FLOORS = Object.freeze({
  1: 0.55,
  2: 0.76,
  3: 0.88,
  4: 0.94,
});

export const SHINY_RATE = 1 / 4096;
export const SHINY_SOFT_PITY_KILLS = 1000;
export const SHINY_MAX_RATE = 1 / 512;
export const SHINY_RATE_GROWTH_KILLS = 1000;
export const SHINY_CARD_ALTAR_COST = 50;

/** Chance-base de encontro shiny pelo tier da espécie no Pokédex. */
export const SHINY_TIER_RATES = Object.freeze({
  Comum: 1 / 4096,
  Incomum: 1 / 3200,
  Raro: 1 / 2400,
  Épico: 1 / 1600,
  Lendário: 1 / 900,
  Mítico: 1 / 500,
});

/**
 * Chance de captura de um shiny com uma ball de força 1. Tiers de espécie
 * mais raros são mais difíceis de capturar; a ball trabalha sobre a falha.
 */
export const SHINY_CAPTURE_TIER_RATES = Object.freeze({
  Comum: 0.35,
  Incomum: 0.29,
  Raro: 0.22,
  Épico: 0.15,
  Lendário: 0.085,
  Mítico: 0.05,
});

/** Pesquisa por espécie: soft pity crescente, limitado e nunca garantido. */
export function shinyChanceForResearch(killsSinceShiny = 0) {
  const kills = Math.max(0, Math.floor(Number(killsSinceShiny) || 0));
  if (kills <= SHINY_SOFT_PITY_KILLS) return SHINY_RATE;
  const extraMultiplier = (kills - SHINY_SOFT_PITY_KILLS) / SHINY_RATE_GROWTH_KILLS;
  return Math.min(SHINY_MAX_RATE, SHINY_RATE * (1 + extraMultiplier));
}

/** Chance final de encontro: maior entre a taxa do tier e o soft-pity. */
export function shinyEncounterChance(speciesRarity = 'Comum', killsSinceShiny = 0, multiplier = 1) {
  const tierRate = SHINY_TIER_RATES[speciesRarity] ?? SHINY_TIER_RATES.Comum;
  const baseChance = Math.max(tierRate, shinyChanceForResearch(killsSinceShiny));
  return clamp(baseChance * Math.max(0, Number(multiplier) || 0), 0, 1);
}

/** Chance fixa de captura shiny por tier × força da ball. */
export function shinyCaptureChance(speciesRarity = 'Comum', ballPower = 1) {
  const base = SHINY_CAPTURE_TIER_RATES[speciesRarity] ?? SHINY_CAPTURE_TIER_RATES.Comum;
  const power = clamp(Number(ballPower) || 1, 1, 100);
  return clamp(1 - Math.pow(1 - base, power), 0, 1);
}

/**
 * Drop de Shiny Cards genéricas. `ctx` aceita level, boss, speciesRarity e
 * cardMultiplier. Bosses não garantem carta, mas têm uma chance muito maior e
 * podem conceder uma segunda carta.
 */
export function rollShinyCardDrop(ctx = {}, random = Math.random) {
  const level = clamp(Math.floor(Number(ctx.level) || 1), 1, 300);
  const rarityFactor = ({ Comum: 1, Incomum: 1.1, Raro: 1.25, Épico: 1.5, Lendário: 1.8, Mítico: 2.2 })[ctx.speciesRarity] || 1;
  const multiplier = clamp(Number(ctx.cardMultiplier) || 1, 0, 10);
  const boss = !!ctx.boss;
  const primaryChance = clamp((boss ? 0.075 + level / 3000 : 0.0025 + level / 120000) * rarityFactor * multiplier, 0, 0.65);
  if (clamp(Number(random()) || 0, 0, 0.999999) >= primaryChance) return 0;
  if (!boss) return 1;
  const secondChance = clamp((0.02 + level / 10000) * rarityFactor * multiplier, 0, 0.3);
  return 1 + (clamp(Number(random()) || 0, 0, 0.999999) < secondChance ? 1 : 0);
}

const IV_KEYS = Object.freeze(['HP', 'ATK', 'DEF', 'VEL', 'ESP', 'RES']);
const IV_KEY_ALIASES = Object.freeze({ HP: 'hp', ATK: 'atk', DEF: 'def', VEL: 'spe', ESP: 'spa', RES: 'spd' });
export const IV_STAT_MAX = 31;
export const IV_TOTAL_MAX = IV_KEYS.length * IV_STAT_MAX;
const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

/**
 * Sorteia um multiplicador contínuo dentro da faixa do tier.
 * Duas rolagens produzem distribuição triangular: o miolo aparece mais que os
 * extremos, e o resultado é arredondado a 2 casas (1.76, 2.41…), nunca "redondo".
 */
function rollTierMult(tier, random) {
  const lo = Number.isFinite(tier.min) ? tier.min : tier.mult;
  const hi = Number.isFinite(tier.max) ? tier.max : tier.mult;
  if (!(hi > lo)) return Math.round(lo * 100) / 100;
  const centered = (clamp(Number(random()) || 0, 0, 0.999999)
    + clamp(Number(random()) || 0, 0, 0.999999)) / 2;
  return Math.round((lo + centered * (hi - lo)) * 100) / 100;
}

export function rollSpecimenRarity(random = Math.random) {
  const totalWeight = WILD_SPECIMEN_RARITIES.reduce((sum, tier) => sum + tier.weight, 0);
  let cursor = clamp(Number(random()) || 0, 0, 0.999999) * totalWeight;
  for (const tier of WILD_SPECIMEN_RARITIES) {
    cursor -= tier.weight;
    if (cursor < 0) return { ...tier, mult: rollTierMult(tier, random) };
  }
  const last = WILD_SPECIMEN_RARITIES.at(-1);
  return { ...last, mult: rollTierMult(last, random) };
}

export function rollIvQualityBand(random = Math.random) {
  let cursor = clamp(Number(random()) || 0, 0, 0.999999) * 100;
  for (const band of IV_QUALITY_BANDS) {
    cursor -= band.weight;
    if (cursor < 0) return band;
  }
  return IV_QUALITY_BANDS.at(-1);
}

function shuffledIvKeys(random) {
  const keys = [...IV_KEYS];
  for (let i = keys.length - 1; i > 0; i--) {
    const j = Math.floor(clamp(Number(random()) || 0, 0, 0.999999) * (i + 1));
    [keys[i], keys[j]] = [keys[j], keys[i]];
  }
  return keys;
}

function distributeIvTotal(total, random) {
  const ivs = {};
  let remaining = clamp(Math.round(total), 0, IV_TOTAL_MAX);
  const keys = shuffledIvKeys(random);
  keys.forEach((key, index) => {
    const slotsLeft = keys.length - index - 1;
    const minimum = Math.max(0, remaining - slotsLeft * IV_STAT_MAX);
    const maximum = Math.min(IV_STAT_MAX, remaining);
    const value = slotsLeft
      ? minimum + Math.floor(clamp(Number(random()) || 0, 0, 0.999999) * (maximum - minimum + 1))
      : remaining;
    ivs[key] = value;
    remaining -= value;
  });
  return ivs;
}

export function ivScore(ivs) {
  return IV_KEYS.reduce((sum, key) => {
    const value = Number(ivs?.[key] ?? ivs?.[IV_KEY_ALIASES[key]]) || 0;
    return sum + clamp(Math.round(value), 0, IV_STAT_MAX);
  }, 0);
}

export function ivPercent(ivs) {
  return Math.round(ivScore(ivs) / IV_TOTAL_MAX * 100);
}

export function rollIvs(random = Math.random) {
  const band = rollIvQualityBand(random);
  // Duas rolagens produzem uma distribuição triangular: os extremos de cada
  // faixa existem, mas o miolo aparece mais, evitando coleções artificiais.
  const centered = (clamp(Number(random()) || 0, 0, 0.999999)
    + clamp(Number(random()) || 0, 0, 0.999999)) / 2;
  const targetPercent = band.min + Math.floor(centered * (band.max - band.min + 1));
  const targetTotal = Math.round(targetPercent / 100 * IV_TOTAL_MAX);
  const ivs = distributeIvTotal(targetTotal, random);
  return { ivs, ivScore: ivScore(ivs), ivTotal: ivPercent(ivs), quality: band.name };
}

/**
 * Chance de captura após o combate.
 *
 * - a tentativa acontece somente depois do nocaute, portanto HP atual não entra;
 * - o nível reduz gradualmente a chance;
 * - o catch rate/estágio da espécie continua importando;
 * - bolas trabalham sobre a chance de falha (1 - (1-p)^força), o que
 *   melhora bastante a Ultra sem transformá-la em Master Ball;
 * - Nv.1–10 comuns recebem um piso de onboarding por bola;
 * - lendários de espécie não recebem essa assistência.
 */
export function captureChance({
  ballPower = 1,
  enemyLevel = 1,
  speciesFactor = 1,
  legendary = false,
  trainerMultiplier = 1,
  keeper = false,
  boss = false,
  zoneSpecial = false,
  ballSlug = null,
} = {}) {
  if (Number(ballPower) >= 100) return 1;
  // Apex Ball: chance fixa ~25% nos especiais de zona (Threat 3/4).
  if (zoneSpecial && ballSlug === 'apexball') {
    return clamp(0.25 * clamp(Number(trainerMultiplier) || 1, 1, 1.25), 0.0025, 0.995);
  }
  const power = clamp(Number(ballPower) || 1, 1, 8);
  const level = Math.max(1, Number(enemyLevel) || 1);
  const rawSpecies = clamp(Number(speciesFactor) || 1, 0.035, 1.15);
  // Espécies comuns mantêm identidade sem criar abismos de 5× no mesmo nível.
  const accessibleSpecies = legendary ? rawSpecies : 0.45 + rawSpecies * 0.55;
  // Lanes B.8: raros/bosses/keepers têm base mais baixa — Super/Ultra passam a valer.
  const hardTarget = legendary || boss || keeper || accessibleSpecies < 0.55;
  const levelBase = hardTarget
    ? 0.42 / (1 + Math.pow(level / 28, 1.05))
    : 0.8 / (1 + Math.pow(level / 32, 0.95));
  const singleBallChance = clamp(levelBase * accessibleSpecies, 0.0025, 0.92);
  let chance = 1 - Math.pow(1 - singleBallChance, power);

  if (!legendary && !boss && level <= 10) {
    chance = Math.max(chance, EARLY_CAPTURE_FLOORS[power] || 0);
  }
  return clamp(chance * clamp(Number(trainerMultiplier) || 1, 1, 1.25), 0.0025, 0.995);
}

/**
 * Escolhe a bola certa para auto-captura (B.8):
 * - lixo/comum → Poké/Great (barata)
 * - raro/boss/keeper/shiny → Super/Ultra (se houver)
 * - Master só se marcada explicitamente
 */
export function pickSmartBall(inventory = {}, target = {}, prefs = {}) {
  const inv = inventory || {};
  const has = (slug) => (Number(inv[slug]) || 0) > 0;
  const hard = !!(target.shiny || target.boss || target.keeper || target.legendary
    || prefs.forceStrong
    || ['Raro', 'Épico', 'Lendário', 'Mítico', 'Celestial'].includes(target.speciesRarity)
    || ['Raro', 'Épico', 'Lendário', 'Mítico', 'Celestial'].includes(target.rarity));
  if (hard) {
    for (const slug of (prefs.strongOrder || ['apexball', 'ultraball', 'superball', 'greatball', 'pokeball'])) {
      if (has(slug)) return slug;
    }
  }
  for (const slug of (prefs.cheapOrder || ['pokeball', 'greatball', 'superball', 'ultraball', 'apexball'])) {
    if (has(slug)) return slug;
  }
  if (prefs.allowMaster && has('masterball')) return 'masterball';
  return null;
}
